import React, { memo } from 'react'
import type { FC, ReactNode } from 'react'

import A from 'components/A'
import styles from './style.module.css'

// http / https のみを対象にする。javascript: 等の他スキームは構造上マッチしない
// 日本語の句読点（。、）は文字クラスに含まれないため、文中の URL でも正しく切れる
// g フラグは付けない（lastIndex を共有しないよう、走査時にコピーを作る）
const URL_PATTERN = /https?:\/\/[\w!#$%&'()*+,\-./:;=?@[\]~]+/

// 文末の句読点は URL に含めない（「〜を参照 https://example.com/manual。」等の誤リンク防止）
const TRAILING_PUNCTUATION = /[.,;:!?]+$/

const CLOSE_TO_OPEN: Record<string, string> = { ')': '(', ']': '[' }

const countChar = (value: string, char: string) => value.split(char).length - 1

/**
 * URL 末尾の記号を落とす。閉じ括弧は「対応する開き括弧が無いときだけ」落とすため、
 * `https://example.com/a_(b)` のような正当な括弧付き URL は壊れない。
 */
const trimTrailingChars = (url: string) => {
  let result = url.replace(TRAILING_PUNCTUATION, '')

  for (;;) {
    const close = result.slice(-1)
    const open = CLOSE_TO_OPEN[close]

    if (!open) break
    if (countChar(result, close) <= countChar(result, open)) break

    result = result.slice(0, -1).replace(TRAILING_PUNCTUATION, '')
  }

  return result
}

type Props = {
  text?: string | null
}

/**
 * テキスト内の URL を新規タブで開くリンクに変換して描画する。
 * ラッパ要素を作らないため、`white-space: pre-wrap` な親要素の中でも改行の見た目が変わらない。
 */
const LinkifiedTextComponent: FC<Props> = ({ text }) => {
  if (!text) return null

  const regex = new RegExp(URL_PATTERN.source, 'g')
  const nodes: ReactNode[] = []
  let lastIndex = 0
  let match: RegExpExecArray | null

  // eslint-disable-next-line no-cond-assign
  while ((match = regex.exec(text)) !== null) {
    const url = trimTrailingChars(match[0])
    const start = match.index

    if (start > lastIndex) nodes.push(text.slice(lastIndex, start))

    nodes.push(
      <A
        key={`${start}-${url}`}
        href={url}
        newTab
        className={styles.link}
        // 伝達事項カードのラッパに既読トグルの onClick があるため、リンクタップで
        // チェックが誤作動しないよう伝播を止める
        onClick={(e: React.MouseEvent<HTMLAnchorElement>) => e.stopPropagation()}
      >
        {url}
      </A>
    )

    lastIndex = start + url.length
    // 末尾記号を URL から外した分、次の走査位置を巻き戻す
    regex.lastIndex = lastIndex
  }

  if (lastIndex < text.length) nodes.push(text.slice(lastIndex))

  return <>{nodes}</>
}

export const LinkifiedText = memo(LinkifiedTextComponent)
